import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, Star } from 'lucide-react';
import { formatActivityDate } from '@utils/helpers';

/**
 * Lists vibes the profile user joined / hosted.
 */
const ProfileVibesSheet = ({ open, onClose, activities = [], onSelect, title = 'Vibes' }) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[1100]" role="presentation">
      <button
        type="button"
        aria-label="Close"
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`absolute inset-x-0 bottom-0 z-10 flex max-h-[80vh] flex-col rounded-t-3xl bg-white shadow-2xl transition-transform duration-300 ease-out dark:bg-[#121826] sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl ${
          entered ? 'translate-y-0 sm:scale-100 sm:opacity-100' : 'translate-y-8 sm:scale-95 sm:opacity-0'
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#E9EEF7] px-5 py-4 dark:border-white/[0.06]">
          <div>
            <h3 className="text-lg font-bold text-surface-900 dark:text-white">{title}</h3>
            <p className="text-xs text-surface-500 dark:text-[#B5BDD1]">
              {activities.length} vibe{activities.length === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-100 text-surface-500 dark:bg-[#1B2435] dark:text-[#B5BDD1]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
          {activities.length === 0 ? (
            <p className="py-12 text-center text-sm text-surface-400">No vibes yet</p>
          ) : (
            <ul className="space-y-2">
              {activities.map((a) => (
                <li key={a._id}>
                  <button
                    type="button"
                    onClick={() => onSelect?.(a)}
                    className="flex w-full items-start gap-3 rounded-2xl border border-[#E9EEF7] bg-surface-50/80 px-3.5 py-3 text-left transition hover:bg-surface-100 dark:border-white/[0.06] dark:bg-[#1B2435]/50 dark:hover:bg-[#1B2435]"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <p className="min-w-0 flex-1 font-semibold text-surface-900 dark:text-white">
                          {a.title}
                          {a.emoji ? ` ${a.emoji}` : ''}
                        </p>
                        {a._isHosted && (
                          <span className="shrink-0 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                            Hosted
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-surface-500 dark:text-[#B5BDD1]">
                        {formatActivityDate(a.dateTime)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-500">
                        <Star size={12} className="fill-amber-400 text-amber-400" />
                        {a.rating || '—'}
                      </span>
                      <ChevronRight size={16} className="text-surface-300 dark:text-surface-600" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ProfileVibesSheet;
